import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { maskCNPJ, maskCEP, onlyDigits } from '@/lib/masks';
import { buscarCep } from '@/services/cep';

export interface DadosEmpresaValues {
  cnpj: string;
  razao_social: string;
  nome_fantasia: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
}

export const emptyDadosEmpresa: DadosEmpresaValues = {
  cnpj: '',
  razao_social: '',
  nome_fantasia: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
};

/** Monta um DadosEmpresaValues a partir de uma Empresa (campos podem vir null). */
export const dadosEmpresaFromEmpresa = (empresa: {
  cnpj?: string | null;
  razao_social?: string | null;
  nome_fantasia?: string | null;
  cep?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
}): DadosEmpresaValues => ({
  cnpj: empresa.cnpj ?? '',
  razao_social: empresa.razao_social ?? '',
  nome_fantasia: empresa.nome_fantasia ?? '',
  cep: empresa.cep ?? '',
  logradouro: empresa.logradouro ?? '',
  numero: empresa.numero ?? '',
  complemento: empresa.complemento ?? '',
  bairro: empresa.bairro ?? '',
  cidade: empresa.cidade ?? '',
  estado: empresa.estado ?? '',
});

interface DadosEmpresaFieldsProps {
  values: DadosEmpresaValues;
  onChange: (patch: Partial<DadosEmpresaValues>) => void;
  idPrefix?: string;
}

export function DadosEmpresaFields({ values, onChange, idPrefix = 'empresa' }: DadosEmpresaFieldsProps) {
  const [buscandoCep, setBuscandoCep] = useState(false);

  const handleCepChange = async (value: string) => {
    const masked = maskCEP(value);
    onChange({ cep: masked });

    if (onlyDigits(masked).length === 8) {
      setBuscandoCep(true);
      const endereco = await buscarCep(masked);
      setBuscandoCep(false);
      if (endereco) {
        onChange({
          logradouro: endereco.logradouro,
          bairro: endereco.bairro,
          cidade: endereco.cidade,
          estado: endereco.estado,
        });
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-cnpj`}>CNPJ</Label>
          <Input
            id={`${idPrefix}-cnpj`}
            value={values.cnpj}
            onChange={(e) => onChange({ cnpj: maskCNPJ(e.target.value) })}
            placeholder="00.000.000/0000-00"
            inputMode="numeric"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-razao_social`}>Razão social</Label>
          <Input
            id={`${idPrefix}-razao_social`}
            value={values.razao_social}
            onChange={(e) => onChange({ razao_social: e.target.value })}
            placeholder="Razão social"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-nome_fantasia`}>Nome fantasia</Label>
        <Input
          id={`${idPrefix}-nome_fantasia`}
          value={values.nome_fantasia}
          onChange={(e) => onChange({ nome_fantasia: e.target.value })}
          placeholder="Nome fantasia"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-cep`}>CEP</Label>
          <div className="relative">
            <Input
              id={`${idPrefix}-cep`}
              value={values.cep}
              onChange={(e) => handleCepChange(e.target.value)}
              placeholder="00000-000"
              inputMode="numeric"
            />
            {buscandoCep && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-logradouro`}>Rua / Logradouro</Label>
          <Input
            id={`${idPrefix}-logradouro`}
            value={values.logradouro}
            onChange={(e) => onChange({ logradouro: e.target.value })}
            placeholder="Rua, avenida..."
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-numero`}>Número</Label>
          <Input
            id={`${idPrefix}-numero`}
            value={values.numero}
            onChange={(e) => onChange({ numero: e.target.value })}
            placeholder="Nº"
          />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-complemento`}>Complemento</Label>
          <Input
            id={`${idPrefix}-complemento`}
            value={values.complemento}
            onChange={(e) => onChange({ complemento: e.target.value })}
            placeholder="Sala, andar..."
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-bairro`}>Bairro</Label>
          <Input
            id={`${idPrefix}-bairro`}
            value={values.bairro}
            onChange={(e) => onChange({ bairro: e.target.value })}
            placeholder="Bairro"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-cidade`}>Cidade</Label>
          <Input
            id={`${idPrefix}-cidade`}
            value={values.cidade}
            onChange={(e) => onChange({ cidade: e.target.value })}
            placeholder="Cidade"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-estado`}>Estado (UF)</Label>
          <Input
            id={`${idPrefix}-estado`}
            value={values.estado}
            onChange={(e) => onChange({ estado: e.target.value.toUpperCase().slice(0, 2) })}
            placeholder="UF"
            maxLength={2}
          />
        </div>
      </div>
    </div>
  );
}
